import { ErrorMessage, Field } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Form, FormControl, Row } from 'react-bootstrap';
import { useToast } from '../../../../../context/ToastContext';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import { NumberInput } from '../../../../../components/inputs/NumberInput';
import { useGetInvoiceQuery } from '../../../../../services/endpoints/finance/invoice';
import { Invoice } from '../../../../../types/finance.types';
import { StepProps } from './AddEditInvoiceItem';

const styles: React.CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: 'none',
  backgroundRepeat: 'no-repeat'
};

export const InvoiceItemForm = ({
  touched,
  errors,
  values,
  setFieldValue,
  invoiceType,
  readOnly
}: StepProps) => {
  const { addToast } = useToast();

  const invoiceId = values?.invoice_id ? Number(values.invoice_id) : undefined;

  const { data, error, isLoading } = useGetInvoiceQuery(invoiceId!, {
    skip: !invoiceId
  });

  const selectedInvoice: Invoice | undefined = data?.invoice;
  const selectedInvoiceType = invoiceType ?? 'Flat Rate';

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading invoice', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  return (
    <>
      <Row>
        <Col xs={12}>
          <FormStepHeader title="Invoice Item Details" showRequiredLegend={!readOnly} />
        </Col>
      </Row>
      <Row>
        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="invoice_id"
          >
            <FormLabel required>Invoice Name</FormLabel>
            <Field
              name="invoice_id"
              as={Form.Select}
              value={values?.invoice_id ?? ''}
              isInvalid={touched.invoice_id && !!errors.invoice_id}
              disabled={true}
              style={styles}
              aria-required
            >
              <option value={values?.invoice_id ?? ''}>
                {selectedInvoice?.name ?? 'Loading...'}
              </option>
            </Field>

            <ErrorMessage name="invoice_id">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="quantity"
          >
            <FormLabel required>Quantity</FormLabel>
            <Field
              name="quantity"
              as={NumberInput}
              isInvalid={touched.quantity && !!errors.quantity}
              className="no-spinner"
              aria-required
            />
            <ErrorMessage name="quantity">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="unit_price"
          >
            <FormLabel required>
              {selectedInvoiceType === 'flat_rate' ? (
                <>Unit Price (Flat Rate Tuition Per Class)</>
              ) : (
                <>
                  Unit Price (# of classes for this session
                  <br />X the tuition price per student)
                </>
              )}
            </FormLabel>

            <Field
              name="unit_price"
              as={NumberInput}
              isInvalid={touched.unit_price && !!errors.unit_price}
              className="no-spinner"
              aria-required
            />
            <ErrorMessage name="unit_price">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="enrollment_id"
          >
            <FormLabel>Enrollment</FormLabel>
            {!selectedInvoice ? (
              <Field
                name="enrollment_id"
                as={Form.Select}
                value={values?.enrollment_id ?? ''}
                disabled
              >
                <option value={values?.enrollment_id ?? ''}>Loading...</option>
              </Field>
            ) : (
              <Field
                name="enrollment_id"
                as={Form.Select}
                value={values?.enrollment_id ?? ''}
                isInvalid={touched.enrollment_id && !!errors.enrollment_id}
              >
                <option value="">Select</option>
                {selectedInvoice.courses?.flatMap((course) =>
                  (course?.enrollments ?? []).map((enrollment) => (
                    <option
                      key={enrollment.id}
                      value={String(enrollment.id)}
                    >
                      {enrollment.label ?? ''}
                    </option>
                  ))
                )}
              </Field>
            )}
            <ErrorMessage name="enrollment_id">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="description"
          >
            <FormLabel>Description</FormLabel>
            <Field
              name="description"
              as="textarea"
              className="form-control"
              rows={4}
            />
            <ErrorMessage name="description">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="invoice_notes"
          >
            <FormLabel>Invoice Notes</FormLabel>
            <Field
              name="invoice_notes"
              as="textarea"
              className="form-control"
              rows={4}
            />
            <ErrorMessage name="invoice_notes">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};
